import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, fmtPrice } from "@/lib/api";
import { ORDER_STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Package, Star, Loader2, CheckCircle2, Clock, X } from "lucide-react";
import MapView from "@/components/MapView";
import { toast } from "sonner";

const STEPS = ["pending", "accepted", "preparing", "ready", "out_for_delivery", "delivered"];

export default function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOrder, setReviewOrder] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [expandedOrders, setExpandedOrders] = useState({});

  const load = async () => {
    const { data } = await api.get("/orders/mine");
    setOrders(Array.isArray(data) ? data.map((order) => order?.data || order) : []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const cancel = async (id) => {
    try { await api.put(`/orders/${id}/status`, { status: "cancelled" }); toast.success("تم إلغاء الطلب"); load(); }
    catch { toast.error("تعذّر الإلغاء"); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { shop_id: reviewOrder.shop_id, order_id: reviewOrder.id, rating, comment });
      toast.success("شكراً لتقييمك!");
      setReviewOrder(null); setComment(""); setRating(5); load();
    } catch { toast.error("تعذّر إرسال التقييم"); }
  };

  if (loading) return <div className="grid min-h-[55vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div data-testid="my-orders" className="mx-auto max-w-4xl">
      <div className="mb-7">
        <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-primary">كل شيء تحت السيطرة</span>
        <h1 className="mt-1 text-3xl font-extrabold tracking-[-0.03em] sm:text-4xl">طلباتي</h1>
        <p className="mt-2 text-sm text-muted-foreground">تابع طلباتك الحالية وسجل طلباتك السابقة.</p>
      </div>
      {orders.length === 0 ? (
        <div className="rounded-[34px] bg-muted py-20 text-center">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-brand text-brand-foreground shadow-brand"><Package className="h-8 w-8" /></div>
          <h2 className="mt-6 text-xl font-extrabold">لا توجد طلبات بعد</h2>
          <p className="mt-2 text-sm text-muted-foreground">أول طلب جميل يبدأ من متجر قريب.</p>
          <Button onClick={() => navigate("/app")} className="mt-6 h-12 rounded-full px-7 font-extrabold">ابدأ التسوّق</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const st = ORDER_STATUS[o.status] || { label: o.status || "حالة غير معروفة", color: "status-pending" };
            const stepIdx = STEPS.indexOf(o.status);
            const isExpanded = expandedOrders[o.id] ?? !["delivered", "cancelled"].includes(o.status);
            return (
              <article key={o.id} data-testid={`order-${o.id}`} className="rounded-[24px] bg-card p-4 shadow-card sm:rounded-[28px] sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="text-[10px] font-bold text-muted-foreground">طلب #{String(o.id).slice(0, 6)}</span>
                    <h3 className="mt-1 text-base font-extrabold sm:text-lg">{o.shop_name}</h3>
                    <p className="mt-1 text-[10px] text-muted-foreground sm:text-[11px]">{new Date(o.created_at).toLocaleString("ar-SY")}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] font-extrabold ${st.color}`}>{st.label}</span>
                </div>

                {o.status !== "cancelled" && (
                  <div className="mt-5 flex items-center gap-1.5 rounded-full bg-muted p-1">
                    {STEPS.map((s, i) => (
                      <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-brand" : "bg-brand/10"}`} />
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setExpandedOrders((current) => ({ ...current, [o.id]: !isExpanded }))}
                  aria-expanded={isExpanded}
                  className="mt-3 flex min-h-10 w-full items-center justify-between border-t border-brand/10 pt-3 text-xs font-bold md:hidden"
                >
                  <span>الإجمالي · <span className="font-num text-primary">{fmtPrice(o.total)}</span></span>
                  <span className="text-primary">{isExpanded ? "إخفاء التفاصيل" : "التفاصيل"}</span>
                </button>

                <div className={`${isExpanded ? "block" : "hidden"} md:block`}>
                <div className="mt-5 space-y-2 rounded-[20px] bg-muted p-4">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex justify-between gap-3 text-xs">
                      <div className="flex items-center gap-3">
                        <img src={it.image_path ? fileUrl(it.image_path) : "/image-placeholder.svg"} alt={it.name} className="h-12 w-12 rounded-lg object-cover" />
                        <div><span>{it.name} × {it.qty}</span>{it.description && <p className="mt-1 text-muted-foreground">{it.description}</p>}</div>
                      </div>
                      <span className="font-num shrink-0 font-bold">{fmtPrice(it.price * it.qty)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <p>عنوان التوصيل: {o.delivery_address}</p>
                  <p>الهاتف: <span dir="ltr">{o.phone}</span></p>
                  <p>قيمة المنتجات: {fmtPrice(o.subtotal)} · التوصيل: {fmtPrice(o.delivery_fee)}</p>
                  <p>الدفع نقدًا عند الاستلام</p>
                  {o.note && <p>ملاحظات: {o.note}</p>}
                </div>
                <div className="mt-4 flex justify-between border-t border-brand/10 pt-4 font-extrabold">
                  <span>الإجمالي</span><span className="font-num text-primary">{fmtPrice(o.total)}</span>
                </div>
                {o.driver_name && o.status === "out_for_delivery" && (
                  <div className="mt-3">
                    <p className="text-sm text-info mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> السائق {o.driver_name} في الطريق إليك</p>
                    {o.driver_location ? (
                      <MapView driver={o.driver_location} destination={o.dest_location} testid={`map-${o.id}`} />
                    ) : (
                      <div className="h-[120px] rounded-2xl border border-border bg-muted/40 grid place-items-center text-sm text-muted-foreground">بانتظار تحديث موقع السائق…</div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  {o.status === "pending" && (
                    <Button data-testid={`cancel-order-${o.id}`} variant="outline" onClick={() => cancel(o.id)} className="rounded-full border-destructive/20 bg-transparent text-xs font-extrabold text-destructive">
                      <X className="w-4 h-4 ms-1" /> إلغاء
                    </Button>
                  )}
                  {o.status === "delivered" && !o.reviewed && (
                    <Button data-testid={`review-order-${o.id}`} onClick={() => setReviewOrder(o)} className="rounded-full text-xs font-extrabold">
                      <Star className="w-4 h-4 ms-1" /> قيّم المتجر
                    </Button>
                  )}
                  {o.status === "delivered" && o.reviewed && (
                    <span className="flex items-center gap-1 text-xs font-bold text-success"><CheckCircle2 className="h-4 w-4" /> تم التقييم</span>
                  )}
                </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!reviewOrder} onOpenChange={(v) => !v && setReviewOrder(null)}>
        <DialogContent dir="rtl" className="rounded-[28px] border-0 bg-background">
          <DialogHeader><DialogTitle>قيّم {reviewOrder?.shop_name}</DialogTitle></DialogHeader>
          <div className="flex justify-center gap-2 py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                <Star className={`w-9 h-9 ${n <= rating ? "fill-rating text-rating" : "text-muted"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="اكتب رأيك (اختياري)" className="rounded-2xl border-0 bg-muted" />
          <Button data-testid="submit-review-btn" onClick={submitReview} className="rounded-full font-extrabold">إرسال التقييم</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
